import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import { buildEventsApiUrl } from '../config/api';
import type { RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';
import { buildJsonAuthHeaders } from '../utils/authFetch';

interface MailboxReply {
  id: string;
  body?: string;
  createdAt?: string;
  senderDisplayName?: string;
}

interface MailboxMessage {
  id: string;
  subject?: string;
  body?: string;
  createdAt?: string;
  eventName?: string;
  senderDisplayName?: string;
  read?: boolean;
  unreadCount?: number;
  replyAllowed?: boolean;
  replies?: MailboxReply[];
}

const formatDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

export default function MailboxPage() {
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = getAuthUserId(user as any);
  const [messages, setMessages] = useState<MailboxMessage[]>([]);
  const [selected, setSelected] = useState<MailboxMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');

  const refreshMailboxBadge = useCallback(async () => {
    const refresh = (window as any).__crowdTradesRefreshMailboxBadge;
    if (typeof refresh === 'function') await refresh();
  }, []);

  const loadMailbox = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox?includeReplies=false`), {
        headers: buildJsonAuthHeaders(userId)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to load your mailbox');
      const nextMessages = data.messages || [];
      setMessages(nextMessages);
      setSelected((current) => nextMessages.find((message: MailboxMessage) => message.id === current?.id) || null);
    } catch (loadError: any) {
      setError(loadError.message || 'Unable to load your mailbox');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadMailbox(); }, [loadMailbox]);

  const openThread = async (thread: MailboxMessage) => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox/${thread.id}`), {
        headers: buildJsonAuthHeaders(userId)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to open this message');
      const opened = data.message as MailboxMessage;
      setSelected(opened);
      setMessages((current) => current.map((message) => message.id === opened.id ? { ...message, ...opened, read: true, unreadCount: 0 } : message));
      if (!opened.read) {
        await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox/${thread.id}/read`), {
          method: 'PATCH', headers: buildJsonAuthHeaders(userId)
        });
      }
      await refreshMailboxBadge();
    } catch (openError: any) {
      setError(openError.message || 'Unable to open this message');
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async () => {
    if (!userId || !selected || !replyDraft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox/${selected.id}/reply`), {
        method: 'POST',
        headers: buildJsonAuthHeaders(userId),
        body: JSON.stringify({ body: replyDraft.trim() })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to send reply');
      setReplyDraft('');
      await openThread(selected);
      await loadMailbox();
    } catch (replyError: any) {
      setError(replyError.message || 'Unable to send reply');
    } finally {
      setBusy(false);
    }
  };

  const deleteThread = async () => {
    if (!userId || !selected) return;
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(buildEventsApiUrl(`/api/users/${userId}/mailbox/${selected.id}`), {
        method: 'DELETE', headers: buildJsonAuthHeaders(userId)
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to delete this message');
      setSelected(null);
      await loadMailbox();
      await refreshMailboxBadge();
    } catch (deleteError: any) {
      setError(deleteError.message || 'Unable to delete this message');
    } finally {
      setBusy(false);
    }
  };

  if (!userId) {
    return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate('/login')}>Log in</Button>}>Log in to view your mailbox.</Alert></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box><Typography variant="h4" fontWeight={700}>Mailbox</Typography><Typography color="text.secondary">Your trade and event conversations, in one place.</Typography></Box>
        <Button onClick={loadMailbox} disabled={loading || busy}>Refresh</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '340px 1fr' }, minHeight: 470 }}>
          <Box sx={{ borderRight: { md: '1px solid #e0e0e0' }, borderBottom: { xs: '1px solid #e0e0e0', md: 0 } }}>
            {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box> : messages.length === 0 ? <Typography color="text.secondary" sx={{ p: 3 }}>No messages yet.</Typography> : (
              <List disablePadding>{messages.map((message) => <ListItemButton key={message.id} selected={selected?.id === message.id} onClick={() => openThread(message)} disabled={busy} sx={{ alignItems: 'flex-start', py: 1.5 }}>
                <ListItemText primary={<Typography fontWeight={message.read ? 500 : 800} noWrap>{message.subject || 'Message'}</Typography>} secondary={<><Typography variant="body2" component="span" noWrap>{message.senderDisplayName || 'CrowdTrades'}{message.eventName ? ` · ${message.eventName}` : ''}</Typography><br />{!message.read && <Typography variant="caption" color="primary" fontWeight={700}>Unread</Typography>}</>} />
              </ListItemButton>)}</List>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {!selected ? <Box sx={{ minHeight: 360, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>Select a conversation to read it.</Box> : <Stack spacing={2}>
              <Box><Typography variant="h6" fontWeight={700}>{selected.subject || 'Message'}</Typography><Typography variant="body2" color="text.secondary">From {selected.senderDisplayName || 'CrowdTrades'} · {formatDate(selected.createdAt)}</Typography></Box>
              <Divider /><Typography sx={{ whiteSpace: 'pre-wrap' }}>{selected.body}</Typography>
              {(selected.replies || []).map((reply) => <Box key={reply.id} sx={{ bgcolor: '#f8f9fc', borderRadius: 2, p: 2 }}><Typography variant="body2" fontWeight={700}>{reply.senderDisplayName || 'Trader'} <Typography component="span" variant="caption" color="text.secondary">· {formatDate(reply.createdAt)}</Typography></Typography><Typography sx={{ whiteSpace: 'pre-wrap', mt: .5 }}>{reply.body}</Typography></Box>)}
              {selected.replyAllowed !== false && <><TextField label="Reply" multiline minRows={3} value={replyDraft} onChange={(event) => setReplyDraft(event.target.value)} /><Stack direction="row" spacing={1} justifyContent="space-between"><Button color="error" startIcon={<DeleteOutlineIcon />} onClick={deleteThread} disabled={busy}>Delete</Button><Button variant="contained" startIcon={<ReplyOutlinedIcon />} onClick={sendReply} disabled={busy || !replyDraft.trim()}>Send reply</Button></Stack></>}
              {selected.read === false && <Button startIcon={<MarkEmailReadOutlinedIcon />} onClick={() => openThread(selected)} disabled={busy}>Mark read</Button>}
            </Stack>}
          </CardContent>
        </Box>
      </Card>
    </Container>
  );
}
